import { WebViewerInstance } from '@pdftron/webviewer';
import { useEffect, useRef } from 'react';

import { PART_FILE_UNITS, PartFileUnits } from '@frontend/api';

export const useUnitsPicker = ({
  instance,
  printUnits,
  onUpdatePrintUnits,
  canUpdatePrintUnits,
  disabledTooltip,
  isVisible = true,
}: {
  instance: WebViewerInstance | null;
  printUnits: PartFileUnits;
  onUpdatePrintUnits?: (units: PartFileUnits) => void;
  canUpdatePrintUnits?: boolean;
  disabledTooltip?: string;
  isVisible?: boolean;
}) => {
  const selectRef = useRef<HTMLSelectElement | null>(null);

  // Latest refs — updated inline on every render so they are always current
  // inside the change event listener, which is attached once at mount.
  const printUnitsRef = useRef(printUnits);
  printUnitsRef.current = printUnits;

  const onUpdateRef = useRef(onUpdatePrintUnits);
  onUpdateRef.current = onUpdatePrintUnits;

  // Mounts (or remounts) the picker when instance or permission-related props
  // change. canUpdatePrintUnits and disabledTooltip are deps so that the
  // Apryse-native title tooltip is correct at construction time. These change
  // infrequently (part navigation, lock state), so the occasional setItems
  // call is acceptable. printUnits changes — the common case — are handled by
  // the update effect below without any setItems call.
  useEffect(() => {
    if (!instance || !isVisible) return;

    const { UI } = instance;
    const canUpdate = canUpdatePrintUnits ?? true;

    const unitsPickerElement = new UI.Components.CustomElement({
      dataElement: 'unitsPicker',
      className: 'units-picker-custom-element',
      ...(!canUpdate && disabledTooltip && { title: disabledTooltip }),
      render: () => {
        const container = document.createElement('div');

        const label = document.createElement('span');
        label.textContent = 'Units';

        const select = document.createElement('select');
        select.disabled = !canUpdate;

        const mmOption = document.createElement('option');
        mmOption.value = PART_FILE_UNITS.MM;
        mmOption.textContent = PART_FILE_UNITS.MM;

        const inOption = document.createElement('option');
        inOption.value = PART_FILE_UNITS.IN;
        inOption.textContent = PART_FILE_UNITS.IN;

        select.appendChild(mmOption);
        select.appendChild(inOption);

        select.value = printUnitsRef.current;

        // Reset the select value immediately so the visual state is always
        // driven by the store. On a failed API call, printUnits won't change
        // in the store — so the update effect won't re-run — leaving the
        // select stuck showing the wrong value. Reverting here ensures the
        // displayed value is only ever advanced by a successful save.
        select.addEventListener('change', (event) => {
          const target = event.target as HTMLSelectElement;
          const newUnits = target.value as PartFileUnits;
          target.value = printUnitsRef.current;
          onUpdateRef.current?.(newUnits);
        });

        container.appendChild(label);
        container.appendChild(select);

        selectRef.current = select;

        return container;
      },
    });

    const defaultHeader = UI.getModularHeader('default-top-header');
    const existingItems = defaultHeader
      .getItems()
      .filter(
        (item: any) =>
          item.dataElement !== 'unitsPicker' &&
          item.dataElement !== 'unitsPickerDivider',
      );

    existingItems.push(
      {
        type: 'divider',
        dataElement: 'unitsPickerDivider',
        hidden: ['mobile'],
      },
      unitsPickerElement,
    );

    defaultHeader.setItems(existingItems);
  }, [instance, isVisible, canUpdatePrintUnits, disabledTooltip]);

  // Updates the select value directly — no setItems call, no header rebuild.
  useEffect(() => {
    if (!selectRef.current) return;
    selectRef.current.value = printUnits;
  }, [printUnits]);
};
